import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faClock, faHourglassHalf, faMobileAlt, faClipboardList, faLock, faSpinner, faUniversity, faCopy, faCloudUploadAlt, faImage, faTrash, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import DOMPurify from 'dompurify';
import { PaymentOrder } from '../types';
import bankLogos from 'thai-banks-logo/banks-logo.json';

const copyToClipboard = (text: string) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(() => legacyCopy(text));
  } else {
    legacyCopy(text);
  }
};

const legacyCopy = (text: string) => {
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.focus();
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

const sanitizeText = (text: string | null | undefined, maxLength: number = 200): string => {
  if (!text) return '';
  const cleaned = DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  return cleaned.slice(0, maxLength);
};

const PaymentPage: React.FC = () => {
  const { t } = useTranslation();
  const { order_number } = useParams();
  const [order, setOrder] = useState<PaymentOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(300);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (order_number) {
      loadOrder();
      const checkInterval = setInterval(checkOrderStatus, 3000);
      return () => clearInterval(checkInterval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_number]);

  useEffect(() => {
    if (order && order.status === 'pending') {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 0) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [order]);

  useEffect(() => {
    if (order && order.status === 'paid' && order.redirect_url) {
      const alreadyRedirected = sessionStorage.getItem(`redirected_${order.order_number}`);
      if (!alreadyRedirected) setRedirectCountdown(5);
    }
  }, [order?.status, order?.redirect_url]);

  useEffect(() => {
    if (redirectCountdown === null) return;
    if (redirectCountdown <= 0) {
      sessionStorage.setItem(`redirected_${order?.order_number}`, '1');
      window.location.href = order?.redirect_url || '#';
      return;
    }
    const timer = setTimeout(() => setRedirectCountdown(prev => (prev ?? 1) - 1), 1000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectCountdown]);

  const loadOrder = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/payment/${order_number}`);
      const result = await response.json();
      if (result.success) {
        setOrder(result.data);
        const expiresAt = new Date(result.data.expires_at);
        const now = new Date();
        const diff = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000));
        setTimeLeft(diff);
      } else {
        toast.error(t('payment.orderNotFound'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const checkOrderStatus = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/payment/${order_number}/status`);
      const result = await response.json();
      if (result.success && result.data.status === 'paid') setOrder(result.data);
    } catch (error) {
      console.error('Error checking status');
    }
  };

  const formatTime = (seconds: number) => {
    // ≥ 1 ชม. แสดง h:mm:ss (setting อายุ slip order ตั้งได้ถึง 180 นาที)
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) { setUploadError(t('payment.fileTypeError')); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError(t('payment.fileSizeError')); return; }
    setUploadError(null);
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadSlip = async () => {
    if (!selectedFile || !previewUrl || !order_number) return;
    setUploading(true);
    setUploadError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/payment/${order_number}/upload-slip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slip_base64: previewUrl }),
      });
      const result = await response.json();
      if (result.success) {
        setUploadSuccess(true);
        // เคส cache เสีย/ธนาคารดีเลย์ backend ตอบ pending → ยังไม่จบ ระบบกำลังตรวจซ้ำให้ (poll สถานะจะจับ paid เอง)
        if (result?.pending) {
          toast.info(result.message);
        } else {
          toast.success(t('payment.uploadSuccess'));
        }
        loadOrder();
      } else {
        setUploadError(result.message || t('payment.uploadError'));
        toast.error(result.message || t('payment.uploadError'));
      }
    } catch (error) {
      setUploadError(t('payment.connectionError'));
      toast.error(t('payment.connectionError'));
    } finally {
      setUploading(false);
    }
  };

  const handleVerifySlip = async () => {
    if (!selectedFile || !previewUrl || !order_number) return;
    setUploading(true);
    setUploadError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/payment/${order_number}/verify-slip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slip_base64: previewUrl }),
      });
      const result = await response.json();
      if (result.success) {
        setUploadSuccess(true);
        if (result?.pending) {
          toast.info(result.message);
        } else {
          toast.success(t('payment.uploadSuccess'));
        }
        loadOrder();
      } else {
        setUploadError(result.message || t('payment.uploadError'));
        toast.error(result.message || t('payment.uploadError'));
      }
    } catch (error) {
      setUploadError((error as Error).message || t('payment.connectionError'));
      toast.error(t('payment.connectionError'));
    } finally {
      setUploading(false);
    }
  };

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '16px', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <FontAwesomeIcon icon={faSpinner} style={{ color: 'white', fontSize: '20px' }} className="animate-spin" />
          </div>
          <p style={{ color: '#94a3b8', fontSize: '13px' }}>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // ─── Not Found ────────────────────────────────────────────────────────────
  if (!order) {
    return (
      <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: 'white', borderRadius: '24px', padding: '40px 32px', textAlign: 'center', maxWidth: '360px', width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '20px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <FontAwesomeIcon icon={faXmark} style={{ fontSize: '28px', color: '#ef4444' }} />
          </div>
          <h2 style={{ color: '#0f172a', fontWeight: 700, fontSize: '18px', marginBottom: '8px' }}>{t('payment.notFoundTitle')}</h2>
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>{t('payment.notFoundDesc')}</p>
        </div>
      </div>
    );
  }

  // ─── Paid ─────────────────────────────────────────────────────────────────
  if (order.status === 'paid') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg,#ecfdf5 0%,#d1fae5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: 'white', borderRadius: '24px', padding: '40px 32px', textAlign: 'center', maxWidth: '360px', width: '100%', boxShadow: '0 8px 40px rgba(16,185,129,0.15)' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg,#10b981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 24px rgba(16,185,129,0.35)' }}>
            <FontAwesomeIcon icon={faCheck} style={{ fontSize: '32px', color: 'white' }} />
          </div>
          <h1 style={{ color: '#0f172a', fontWeight: 800, fontSize: '22px', marginBottom: '6px' }}>{t('payment.paidTitle')}</h1>
          <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '24px' }}>{t('payment.paidThankYou')}</p>
          <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '16px', marginBottom: '24px' }}>
            {[
              { label: t('payment.orderNumber'), value: sanitizeText(order.order_number, 50), color: '#0f172a' },
              { label: t('payment.amount'), value: `฿${order.total_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`, color: '#10b981', large: true },
              { label: t('payment.paidWhen'), value: order.paid_at ? new Date(order.paid_at).toLocaleString('th-TH') : '-', color: '#0f172a' },
            ].map(({ label, value, color, large }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#94a3b8', fontSize: '12px' }}>{label}</span>
                <span style={{ color, fontWeight: 700, fontSize: large ? '18px' : '13px' }}>{value}</span>
              </div>
            ))}
          </div>
          {order.redirect_url && redirectCountdown !== null && (
            <div>
              <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '12px' }}>กำลังกลับไปยังร้านค้า ใน {redirectCountdown} วินาที...</p>
              <button onClick={() => { window.location.href = order.redirect_url || '#'; }}
                style={{ width: '100%', padding: '14px', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', fontWeight: 700, fontSize: '15px', cursor: 'pointer' }}>
                กลับเลย →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Expired ──────────────────────────────────────────────────────────────
  if (order.status === 'expired' && !order.slip_only) {
    return (
      <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: 'white', borderRadius: '24px', padding: '32px 24px', maxWidth: '360px', width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '18px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <FontAwesomeIcon icon={faClock} style={{ fontSize: '24px', color: '#f59e0b' }} />
            </div>
            <h2 style={{ color: '#0f172a', fontWeight: 700, fontSize: '18px', marginBottom: '6px' }}>{t('payment.expiredTitle')}</h2>
            <p style={{ color: '#64748b', fontSize: '13px' }}>{t('payment.expiredDesc')}</p>
          </div>

          {order.customer_slip_upload_enabled && (
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
              <p style={{ color: '#94a3b8', fontSize: '12px', textAlign: 'center', marginBottom: '16px' }}>{t('payment.expiredSlipHint')}</p>
              {uploadSuccess ? (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', padding: '20px', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '32px', color: '#10b981', marginBottom: '8px' }} />
                  <p style={{ color: '#065f46', fontWeight: 700 }}>{t('payment.uploadSuccess')}</p>
                  <p style={{ color: '#047857', fontSize: '12px', marginTop: '4px' }}>{t('payment.uploadChecking')}</p>
                </div>
              ) : !selectedFile ? (
                <div onClick={() => fileInputRef.current?.click()}
                  style={{ border: '2px dashed #a5b4fc', borderRadius: '16px', padding: '28px 16px', textAlign: 'center', cursor: 'pointer', background: '#eef2ff' }}>
                  <FontAwesomeIcon icon={faImage} style={{ fontSize: '28px', color: '#818cf8', marginBottom: '8px' }} />
                  <p style={{ color: '#4f46e5', fontWeight: 600, fontSize: '14px' }}>{t('payment.tapToSelect')}</p>
                  <p style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>{t('payment.fileTypes')}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ position: 'relative', borderRadius: '14px', overflow: 'hidden' }}>
                    <img src={previewUrl || ''} alt="Preview" style={{ width: '100%', maxHeight: '220px', objectFit: 'contain', background: '#f8fafc', display: 'block' }} />
                    <button onClick={clearSelectedFile} style={{ position: 'absolute', top: '8px', right: '8px', width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: '11px' }} />
                    </button>
                  </div>
                  <button onClick={handleUploadSlip} disabled={uploading}
                    style={{ padding: '14px', borderRadius: '14px', border: 'none', background: uploading ? '#94a3b8' : 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: 'white', fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1 }}>
                    {uploading
                      ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin" style={{ marginRight: '8px' }} />{t('payment.checkingSlip')}</>
                      : <><FontAwesomeIcon icon={faCloudUploadAlt} style={{ marginRight: '8px' }} />{t('payment.confirmPayment')}</>
                    }
                  </button>
                </div>
              )}
              {uploadError && (
                <div style={{ marginTop: '10px', background: '#fef2f2', borderRadius: '12px', padding: '10px 14px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ef4444', fontSize: '13px', marginTop: '2px', flexShrink: 0 }} />
                  <p style={{ color: '#dc2626', fontSize: '13px' }}>{uploadError}</p>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFileSelect} className="hidden" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const bankCode = order.banks?.bank_code;
  const bankLogoEntry = bankCode ? (bankLogos as Record<string, { icon: string; name: string }>)[bankCode] : null;

  // Shared: slip upload UI block (inline render to avoid nested-component issues)
  const renderSlipUpload = (onSubmit: () => void) => (
    <div style={{ marginTop: '0' }}>
      {uploadSuccess ? (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', padding: '20px', textAlign: 'center' }}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '36px', color: '#10b981', display: 'block', margin: '0 auto 8px' }} />
          <p style={{ color: '#065f46', fontWeight: 700 }}>{t('payment.uploadSuccess')}</p>
          <p style={{ color: '#047857', fontSize: '12px', marginTop: '4px' }}>{t('payment.uploadChecking')}</p>
        </div>
      ) : !selectedFile ? (
        <>
          {/* Tap-to-upload zone — prominent, animated border */}
          <div onClick={() => fileInputRef.current?.click()}
            className="pp-upload-tap"
            style={{
              border: '2.5px dashed #818cf8',
              borderRadius: '18px',
              padding: '28px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg,#eef2ff 0%,#f5f3ff 100%)',
              minHeight: '150px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.15s',
            }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px', boxShadow: '0 4px 16px rgba(99,102,241,0.35)' }}>
              <FontAwesomeIcon icon={faCloudUploadAlt} style={{ color: 'white', fontSize: '22px' }} />
            </div>
            <p style={{ color: '#4338ca', fontWeight: 700, fontSize: '16px', marginBottom: '4px' }}>{t('payment.tapToSelectTransfer')}</p>
            <p style={{ color: '#a5b4fc', fontSize: '12px' }}>{t('payment.fileTypes')}</p>
          </div>
          <div style={{ marginTop: '10px', background: '#fffbeb', borderRadius: '10px', padding: '8px 12px', display: 'flex', gap: '6px', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b', fontSize: '11px', flexShrink: 0 }} />
            <p style={{ color: '#92400e', fontSize: '11px' }}>{t('payment.slipTimeWarning')}</p>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ position: 'relative', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
            <img src={previewUrl || ''} alt="Slip Preview" style={{ width: '100%', maxHeight: '260px', objectFit: 'contain', background: '#f8fafc', display: 'block' }} />
            <button onClick={clearSelectedFile}
              style={{ position: 'absolute', top: '8px', right: '8px', width: '34px', height: '34px', borderRadius: '50%', border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(239,68,68,0.4)' }}>
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
            </button>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '6px 10px', background: 'linear-gradient(transparent,rgba(0,0,0,0.45))' }}>
              <p style={{ color: 'white', fontSize: '11px' }}>{selectedFile.name}</p>
            </div>
          </div>
          <button onClick={onSubmit} disabled={uploading}
            style={{ padding: '16px', borderRadius: '16px', border: 'none', background: uploading ? '#94a3b8' : 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)', color: 'white', fontWeight: 800, fontSize: '16px', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1, boxShadow: uploading ? 'none' : '0 6px 20px rgba(79,70,229,0.4)', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {uploading
              ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin" />{t('payment.checkingSlip')}</>
              : <><FontAwesomeIcon icon={faCheckCircle} />{t('payment.confirmPayment')}</>
            }
          </button>
        </div>
      )}
      {uploadError && (
        <div style={{ marginTop: '10px', background: '#fef2f2', borderRadius: '12px', padding: '10px 14px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#ef4444', fontSize: '13px', marginTop: '2px', flexShrink: 0 }} />
          <p style={{ color: '#dc2626', fontSize: '13px' }}>{uploadError}</p>
        </div>
      )}
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFileSelect} className="hidden" />
    </div>
  );

  // ─── Main Render ──────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes ppPulse {
          0%,100% { border-color: #818cf8; }
          50% { border-color: #4f46e5; box-shadow: 0 0 0 6px rgba(99,102,241,0.08); }
        }
        .pp-upload-tap { animation: ppPulse 2.8s ease-in-out infinite; }
        .pp-upload-tap:active { transform: scale(0.98); }
        .pp-copy-btn:active { transform: scale(0.93); }
      `}</style>

      <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>

        {/* ── Header ─────────────────────────────────────── */}
        <div style={{ background: 'linear-gradient(135deg,#1e1b4b 0%,#312e81 55%,#4338ca 100%)', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FontAwesomeIcon icon={faLock} style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }} />
            </div>
            <span style={{ color: 'white', fontWeight: 800, fontSize: '17px', letterSpacing: '-0.3px' }}>123PAY</span>
          </div>
          {timeLeft > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.12)', borderRadius: '10px', padding: '5px 11px', backdropFilter: 'blur(8px)' }}>
              <FontAwesomeIcon icon={faHourglassHalf} style={{ color: '#fbbf24', fontSize: '11px' }} />
              <span style={{ color: 'white', fontWeight: 700, fontFamily: 'monospace', fontSize: '15px' }}>{formatTime(timeLeft)}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(239,68,68,0.2)', borderRadius: '10px', padding: '5px 11px' }}>
              <FontAwesomeIcon icon={faClock} style={{ color: '#fca5a5', fontSize: '11px' }} />
              <span style={{ color: '#fca5a5', fontWeight: 600, fontSize: '12px' }}>{t('payment.timeout')}</span>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 16px', maxWidth: '500px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>

          {/* ── Amount Card ─────────────────────────────── */}
          <div style={{ background: 'white', borderRadius: '20px', padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ color: '#94a3b8', fontSize: '11px', display: 'block', marginBottom: '2px' }}>{t('payment.amountToPay')}</span>
              <span style={{ color: '#1e1b4b', fontWeight: 800, fontSize: '30px', letterSpacing: '-1px', lineHeight: 1.1, display: 'block' }}>
                ฿{order.total_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
              </span>
              <span style={{ color: '#cbd5e1', fontSize: '10px' }}>{t('payment.amountNote')}</span>
            </div>
            <button className="pp-copy-btn"
              onClick={() => { copyToClipboard(order.total_amount.toFixed(2)); toast.success(t('common.copiedAmount')); }}
              style={{ background: '#eef2ff', border: 'none', borderRadius: '12px', padding: '10px 14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', transition: 'transform 0.15s' }}>
              <FontAwesomeIcon icon={faCopy} style={{ color: '#6366f1', fontSize: '16px' }} />
              <span style={{ color: '#6366f1', fontSize: '10px', fontWeight: 700 }}>คัดลอก</span>
            </button>
          </div>

          {/* ── Slip-Only Mode ──────────────────────────── */}
          {order.slip_only ? (
            <>
              {/* Bank Info — compact single card */}
              {order.banks && (
                <div style={{ background: 'white', borderRadius: '20px', padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    {bankLogoEntry && (
                      <img src={bankLogoEntry.icon} alt={bankCode} style={{ width: '34px', height: '34px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ color: '#94a3b8', fontSize: '10px' }}>{t('common.bank')}</span>
                      <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '14px', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{order.banks.name || order.banks.bank_code}</span>
                    </div>
                    <span style={{ color: '#94a3b8', fontSize: '12px', flexShrink: 0 }}>{order.banks.bank_code}</span>
                  </div>
                  <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <span style={{ color: '#94a3b8', fontSize: '10px', display: 'block' }}>{t('common.bankAccount')}</span>
                      <span style={{ color: '#1e1b4b', fontWeight: 800, fontSize: '22px', fontFamily: 'monospace', letterSpacing: '0.5px' }}>{order.banks.account_number}</span>
                    </div>
                    <button className="pp-copy-btn"
                      onClick={() => { copyToClipboard(order.banks!.account_number); toast.success(t('common.copiedAccount')); }}
                      style={{ background: '#e0e7ff', border: 'none', borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'transform 0.15s' }}>
                      <FontAwesomeIcon icon={faCopy} style={{ color: '#6366f1', fontSize: '14px' }} />
                      <span style={{ color: '#6366f1', fontSize: '9px', fontWeight: 700 }}>คัดลอก</span>
                    </button>
                  </div>
                  <p style={{ color: '#e2e8f0', fontSize: '10px', textAlign: 'center', marginTop: '8px' }}>#{sanitizeText(order.order_number, 50)}</p>
                </div>
              )}

              {/* Upload Slip — hero section, always visible */}
              <div style={{ background: 'white', borderRadius: '20px', padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '8px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FontAwesomeIcon icon={faCloudUploadAlt} style={{ color: 'white', fontSize: '11px' }} />
                  </div>
                  <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '15px' }}>{t('payment.attachSlip')}</span>
                </div>
                <p style={{ color: '#94a3b8', fontSize: '11px', marginBottom: '12px', paddingLeft: '34px' }}>{t('payment.afterTransfer')}</p>
                {renderSlipUpload(handleVerifySlip)}
              </div>
            </>

          ) : order.qr_code_url ? (
            <>
              {/* QR Code */}
              <div style={{ background: 'white', borderRadius: '20px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
                  <FontAwesomeIcon icon={faMobileAlt} style={{ color: '#6366f1', fontSize: '15px' }} />
                  <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '15px' }}>{t('payment.scanQrToPay')}</span>
                </div>
                <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '16px' }}>{t('payment.useAppToScan')}</p>
                {timeLeft > 0 ? (
                  <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '16px', display: 'inline-block' }}>
                    <img src={order.qr_code_url} alt="QR Code" style={{ width: '100%', maxWidth: '240px', borderRadius: '10px', display: 'block' }} />
                  </div>
                ) : (
                  <div style={{ padding: '32px', textAlign: 'center' }}>
                    <FontAwesomeIcon icon={faClock} style={{ fontSize: '40px', color: '#e2e8f0', marginBottom: '10px', display: 'block' }} />
                    <p style={{ color: '#64748b', fontWeight: 700, marginBottom: '4px' }}>{t('payment.timeoutTitle')}</p>
                    <p style={{ color: '#94a3b8', fontSize: '13px' }}>{t('payment.timeoutDesc')}</p>
                  </div>
                )}
                <p style={{ color: '#e2e8f0', fontSize: '10px', marginTop: '10px' }}>#{sanitizeText(order.order_number, 50)}</p>
              </div>
              {!order.slip_only && order.customer_slip_upload_enabled && (
                <div style={{ background: 'white', borderRadius: '20px', padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '8px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faCloudUploadAlt} style={{ color: 'white', fontSize: '11px' }} />
                    </div>
                    <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '15px' }}>{t('payment.attachSlip')}</span>
                  </div>
                  {renderSlipUpload(handleUploadSlip)}
                </div>
              )}
            </>

          ) : order.banks ? (
            <>
              {/* Bank Transfer (non-slip-only) */}
              <div style={{ background: 'white', borderRadius: '20px', padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                  <FontAwesomeIcon icon={faUniversity} style={{ color: '#6366f1', fontSize: '15px' }} />
                  <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '15px' }}>{t('payment.bankTransfer')}</span>
                </div>
                {timeLeft > 0 ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      {bankLogoEntry && <img src={bankLogoEntry.icon} alt={bankCode} style={{ width: '34px', height: '34px', borderRadius: '10px', objectFit: 'cover' }} />}
                      <div>
                        <span style={{ color: '#94a3b8', fontSize: '10px' }}>{t('common.bank')}</span>
                        <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '14px', display: 'block' }}>{order.banks.bank_code}</span>
                      </div>
                    </div>
                    <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '12px 16px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <span style={{ color: '#94a3b8', fontSize: '10px', display: 'block' }}>{t('common.bankAccount')}</span>
                        <span style={{ color: '#1e1b4b', fontWeight: 800, fontSize: '20px', fontFamily: 'monospace' }}>{order.banks.account_number}</span>
                      </div>
                      <button className="pp-copy-btn"
                        onClick={() => { if (order.banks?.account_number) { copyToClipboard(order.banks.account_number); toast.success(t('common.copiedAccount')); } }}
                        style={{ background: '#e0e7ff', border: 'none', borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'transform 0.15s' }}>
                        <FontAwesomeIcon icon={faCopy} style={{ color: '#6366f1', fontSize: '14px' }} />
                        <span style={{ color: '#6366f1', fontSize: '9px', fontWeight: 700 }}>คัดลอก</span>
                      </button>
                    </div>
                    <div style={{ background: '#eef2ff', border: '2px solid #c7d2fe', borderRadius: '14px', padding: '12px 16px', marginBottom: '8px' }}>
                      <span style={{ color: '#6366f1', fontSize: '11px', fontWeight: 600, display: 'block', textAlign: 'center', marginBottom: '4px' }}>{t('payment.amountToTransfer')}</span>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <span style={{ color: '#312e81', fontWeight: 800, fontSize: '22px' }}>฿{order.total_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                        <button onClick={() => { copyToClipboard(order.total_amount.toFixed(2)); toast.success(t('common.copiedAmount')); }}
                          style={{ background: '#c7d2fe', border: 'none', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer' }}>
                          <FontAwesomeIcon icon={faCopy} style={{ color: '#6366f1', fontSize: '12px' }} />
                        </button>
                      </div>
                    </div>
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '7px 12px' }}>
                      <p style={{ color: '#ef4444', fontSize: '11px', textAlign: 'center', fontWeight: 600 }}>{t('payment.transferExact')}</p>
                    </div>
                    <p style={{ color: '#e2e8f0', fontSize: '10px', textAlign: 'center', marginTop: '8px' }}>#{sanitizeText(order.order_number, 50)}</p>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '28px' }}>
                    <FontAwesomeIcon icon={faClock} style={{ fontSize: '40px', color: '#e2e8f0', marginBottom: '10px', display: 'block' }} />
                    <p style={{ color: '#64748b', fontWeight: 700, marginBottom: '4px' }}>{t('payment.timeoutTitle')}</p>
                    <p style={{ color: '#94a3b8', fontSize: '13px' }}>{t('payment.timeoutDesc')}</p>
                  </div>
                )}
              </div>
              {!order.slip_only && order.customer_slip_upload_enabled && (
                <div style={{ background: 'white', borderRadius: '20px', padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '8px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faCloudUploadAlt} style={{ color: 'white', fontSize: '11px' }} />
                    </div>
                    <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '15px' }}>{t('payment.attachSlip')}</span>
                  </div>
                  {renderSlipUpload(handleUploadSlip)}
                </div>
              )}
            </>

          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>{t('payment.cannotLoadPayment')}</div>
          )}

          {/* ── How to Pay ─────────────────────────────── */}
          <div style={{ background: 'white', borderRadius: '20px', padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <FontAwesomeIcon icon={faClipboardList} style={{ color: '#6366f1', fontSize: '13px' }} />
              <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '13px' }}>{t('payment.howToPay')}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(order.qr_code_url
                ? t('payment.stepsQr', { returnObjects: true }) as string[]
                : t('payment.stepsBank', { returnObjects: true }) as string[]
              ).map((step, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ minWidth: '20px', height: '20px', borderRadius: '6px', background: '#eef2ff', color: '#6366f1', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>{idx + 1}</span>
                  <span style={{ color: '#475569', fontSize: '13px', lineHeight: 1.5 }}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Footer ─────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px 0 4px' }}>
            <FontAwesomeIcon icon={faLock} style={{ color: '#10b981', fontSize: '11px' }} />
            <span style={{ color: '#94a3b8', fontSize: '11px' }}>
              {order.qr_code_url ? t('payment.secureQr') : t('payment.secureBank')}
            </span>
          </div>
          <p style={{ textAlign: 'center', color: '#cbd5e1', fontSize: '10px', paddingBottom: '24px' }}>Powered by 123PAY</p>

        </div>
      </div>
    </>
  );
};

export default PaymentPage;
